/**
 * Email API Endpoints
 * Centralizes all email-related API calls. `id` = the email's documentId.
 */
import type { AxiosResponse } from "axios";
import api from "../api";
import type { EntityPayload, QueryParams } from "../../types/api";

export const emailApi = {
  /** Send email(s) as a JSON payload. */
  send: (data: EntityPayload): Promise<AxiosResponse> => api.post("/api/sendemails", { data }),

  /**
   * Send email(s) as multipart/form-data so disk-file attachments (appended as
   * `files.attachments`) reach the server. `form` is a FormData carrying a JSON
   * "data" part plus zero or more "files.attachments" file parts — the path the
   * compose modal uses. axios v1 sets the multipart boundary automatically.
   */
  sendForm: (form: FormData): Promise<AxiosResponse> =>
    api.post("/api/sendemails", form, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  getAll: (params: QueryParams = {}): Promise<AxiosResponse> =>
    api.get("/api/sendemails", { params }),

  export: (data: EntityPayload): Promise<AxiosResponse> => api.post("/api/export/email", { data }),

  getLists: (params: QueryParams = {}): Promise<AxiosResponse> =>
    api.get("/api/export/getlists", { params }),

  delete: (id: string): Promise<AxiosResponse> => api.delete(`/api/sendemails/${id}`),

  bulkDelete: (emailIds: Array<string | number>): Promise<AxiosResponse> =>
    api.post("/api/sendemails/bulkdelete", {
      data: { emailentries: emailIds },
    }),

  /** Resend an email to a new address. */
  resend: (id: string, newEmail: string): Promise<AxiosResponse> =>
    api.post("/api/sendemails/resend", { id, newEmail }),
};
